import cloneDeep from 'lodash/cloneDeep';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { isHcpExpansionWorkspace } from '../helpers/routes';
import { queryClient } from '../lib';
import type { GetFilterConfigQueryResponse } from '../lib/api/filterConfig';
import { WorkspaceTypeID } from '../lib/api/workspaces';
import { STORE_NAMES } from './constants';

export type ExpressionType = 'target' | 'block' | 'yesno';

export interface Filter {
  expression: string;
  filterName: string;
  enabled: boolean;
  interactive?: boolean;
  visible?: boolean;
  required?: boolean;
  expressionType?: ExpressionType;
  advertiserId?: number | undefined;
  dashboardSlug?: string | null;
}

export interface FiltersState {
  filters: Record<string, Filter>;
  draftFilters: Record<string, Filter>;
  draftResetKey?: number;
  setFilter: (filter: Filter) => void;
  setDraftFilter: (filter: Filter) => void;
  clearDraftFilter: (filterName?: string) => void;
  setMultipleFilters: (filters: Filter[]) => void;
  setFilterExpressionType: (payload: { filterName: string; expressionType: ExpressionType }) => void;
  toggleFilter: (payload: { filterName: string; enabled: boolean }) => void;
  clearFiltersState: (payload: { resetAll?: boolean; workspaceTypeId?: WorkspaceTypeID }) => void;
  setFilterExpression: (payload: { filterName: string; expression: string }) => void;
  setAdvertiserDashboardSlugId: (payload: { slugId: string }) => void;
}
export const initialFiltersState = {
  filters: {},
  draftFilters: {},
};

const getPreservedFilters = (workspaceTypeId: WorkspaceTypeID, state: FiltersState) => {
  const isExpansionWorkspace = isHcpExpansionWorkspace(workspaceTypeId);
  const queryKey = ['filterConfig', { workspaceTypeId }];
  //TODO: This is an antipattern, but we do not have access to fetched data in the slice
  // Potential fix could be to send filter configs in action payload
  const { filterConfig } = queryClient.getQueryData<GetFilterConfigQueryResponse>(queryKey) || {};

  return (
    filterConfig?.reduce(
      (acc, f) => {
        const shouldPreserve = isExpansionWorkspace
          ? f.filterToPreserveOnReset || f.type?.includes('accordion_list')
          : f.filterToPreserveOnReset;

        if (shouldPreserve && state.filters[f.filterName]) {
          acc[f.filterName] = { ...state.filters[f.filterName] };
        }

        return acc;
      },
      {} as Record<string, Filter>,
    ) || {}
  );
};

export const useFiltersStore = create<FiltersState>()(
  devtools(
    (set) => ({
      ...initialFiltersState,
      setFilter: (filter) =>
        set(
          (state) => {
            const { filterName, expression, enabled, expressionType, advertiserId, dashboardSlug } = filter;
            return {
              filters: {
                ...state.filters,
                [filterName]: {
                  filterName,
                  expression,
                  enabled,
                  expressionType,
                  advertiserId,
                  dashboardSlug,
                },
              },
            };
          },
          false,
          'filters/setFilter',
        ),
      setDraftFilter: (filter) =>
        set(
          (state) => {
            const { filterName, expression, enabled, expressionType, advertiserId, dashboardSlug } = filter;
            return {
              draftFilters: {
                ...state.draftFilters,
                [filterName]: {
                  filterName,
                  expression,
                  enabled,
                  expressionType,
                  advertiserId,
                  dashboardSlug,
                },
              },
            };
          },
          false,
          'filters/setDraftFilter',
        ),
      clearDraftFilter: (filterName?: string) =>
        set(
          (state) => {
            if (!filterName) return { draftFilters: {}, draftResetKey: (state.draftResetKey ?? 0) + 1 };
            const { [filterName]: _, ...rest } = state.draftFilters;
            return { draftFilters: rest };
          },
          false,
          'filters/clearDraftFilter',
        ),
      setMultipleFilters: (filters) =>
        set(
          (state) => {
            const tempFilters = cloneDeep(state.filters);
            filters.forEach((filter) => {
              const { filterName, expression, enabled, expressionType } = filter;
              tempFilters[filterName] = {
                filterName: filterName,
                expression,
                enabled: enabled,
                expressionType: expressionType,
              };
            });
            return {
              filters: tempFilters,
            };
          },
          false,
          'filters/setMultipleFilters',
        ),
      setFilterExpressionType: (payload) =>
        set(
          (state) => {
            const { filterName, expressionType } = payload;
            return {
              filters: {
                ...state.filters,
                [filterName]: {
                  ...state.filters[filterName],
                  expressionType: expressionType,
                },
              },
            };
          },
          false,
          'filters/setFilterExpressionType',
        ),
      toggleFilter: (payload) =>
        set(
          (state) => {
            const { filterName, enabled } = payload;
            return {
              filters: {
                ...state.filters,
                [filterName]: {
                  ...state.filters[filterName],
                  enabled: enabled,
                },
              },
            };
          },
          false,
          'filters/toggleFilter',
        ),
      clearFiltersState: (payload) =>
        set(
          (state) => {
            const { resetAll = true, workspaceTypeId = WorkspaceTypeID.HCPExplorer } = payload;

            if (resetAll) {
              return initialFiltersState;
            }

            const preservedFilters = getPreservedFilters(workspaceTypeId, state);

            return {
              filters: preservedFilters,
            };
          },
          false,
          'filters/clearFiltersState',
        ),
      setFilterExpression: (payload) =>
        set(
          (state) => {
            const { filterName, expression } = payload;
            return {
              filters: {
                ...state.filters,
                [filterName]: {
                  ...state.filters[filterName],
                  expression: expression,
                },
              },
            };
          },
          false,
          'filters/setFilterExpression',
        ),
      setAdvertiserDashboardSlugId: (payload) =>
        set(
          (state) => {
            const val = 'advertiser_filter';
            const { slugId } = payload;

            return {
              filters: {
                ...state.filters,
                [val]: {
                  ...state.filters[val],
                  dashboardSlug: slugId,
                },
              },
            };
          },
          false,
          'filters/setAdvertiserDashboardSlugId',
        ),
    }),
    {
      name: STORE_NAMES.FILTERS,
      enabled: process.env.NODE_ENV === 'development',
    },
  ),
);
